import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Vote, ArrowRight, Check, Lock, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * FeaturedPoll — Home-page widget that surfaces the most recent active poll.
 * Compact card with inline vote form. Falls back to nothing (null) when no
 * polls are active, so the Home page degrades gracefully.
 */
const FeaturedPoll = () => {
    const [poll, setPoll] = useState(null);
    const [loading, setLoading] = useState(true);
    const [optionId, setOptionId] = useState("");
    const [form, setForm] = useState({ voter_name: "", voter_email: "", voter_phone: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/polls")
            .then((r) => {
                const open = (r.data || []).filter((p) => p.is_open);
                if (open.length) setPoll(open[0]);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading || !poll) return null;

    const total = poll.total_votes ?? 0;
    const showCounts = poll.options.some((o) => typeof o.votes === "number");

    const submit = async (e) => {
        e.preventDefault();
        if (!optionId) { toast.error("Pick an option"); return; }
        if (!form.voter_name.trim() || !form.voter_email.trim()) {
            toast.error("Name and email are required"); return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/polls/${poll.id}/vote`, {
                option_id: optionId,
                voter_name: form.voter_name.trim(),
                voter_email: form.voter_email.trim(),
                voter_phone: form.voter_phone.trim() || null,
            });
            setPoll(data);
            setDone(true);
            toast.success("Thanks — your vote was counted.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit vote");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="bg-[#FDFBF7] border-t border-black/5" data-testid="home-featured-poll">
            <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
                    <div className="lg:col-span-2">
                        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Community voice</p>
                        <h2 className="font-heading text-3xl sm:text-4xl text-[#1C252E] tracking-tight leading-[1.1]">{poll.question}</h2>
                        {poll.description && <p className="text-sm text-[#4A5568] mt-3">{poll.description}</p>}
                        <p className="text-xs text-[#4A5568] mt-4 flex items-center gap-1.5"><Lock className="w-3 h-3" strokeWidth={2} /> Your contact info is private — only admins see who voted.</p>
                        <Link to="/polls" data-testid="home-featured-poll-all-link" className="inline-flex items-center gap-1 text-sm font-medium text-[#E53935] hover:underline mt-3">
                            See all open polls <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                        </Link>
                    </div>

                    <div className="lg:col-span-3 bg-white border border-black/10 rounded-2xl p-6">
                        {done ? (
                            <div data-testid="home-featured-poll-thanks">
                                <p className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-green-700 bg-green-50 px-3 py-1 rounded-full mb-4">
                                    <Check className="w-3.5 h-3.5" strokeWidth={2} /> Thanks for voting
                                </p>
                                {showCounts ? (
                                    <ul className="space-y-2">
                                        {poll.options.map((o) => {
                                            const pct = total ? Math.round(((o.votes || 0) / total) * 100) : 0;
                                            return (
                                                <li key={o.id}>
                                                    <div className="flex justify-between text-sm mb-1">
                                                        <span className="text-[#1C252E]">{o.label}</span>
                                                        <span className="text-[#4A5568]">{o.votes || 0} · {pct}%</span>
                                                    </div>
                                                    <div className="h-2 bg-[#F4F1EA] rounded-full overflow-hidden"><div className="h-full bg-[#E53935] transition-all" style={{ width: `${pct}%` }} /></div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-[#4A5568] italic flex items-center gap-2"><Lock className="w-3.5 h-3.5" strokeWidth={2} /> Results will be published when the poll closes.</p>
                                )}
                                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mt-3"><BarChart3 className="inline w-3 h-3 -mt-0.5" /> {total} total votes</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-3" data-testid="home-featured-poll-form">
                                <fieldset className="space-y-1.5">
                                    {poll.options.map((o) => (
                                        <label key={o.id} className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition ${optionId === o.id ? "bg-[#F2D8D5]/50 border-[#E53935]" : "bg-white border-black/10 hover:border-[#E53935]/40"}`}>
                                            <input type="radio" name="featured-poll" value={o.id} checked={optionId === o.id} onChange={() => setOptionId(o.id)} data-testid={`home-poll-option-${o.id}`} className="accent-[#E53935]" />
                                            <span className="text-sm text-[#1C252E]">{o.label}</span>
                                        </label>
                                    ))}
                                </fieldset>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    <input data-testid="home-poll-name" value={form.voter_name} onChange={(e) => setForm({ ...form, voter_name: e.target.value })} placeholder="Name *" required className="rounded-xl border border-black/10 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                    <input data-testid="home-poll-email" type="email" value={form.voter_email} onChange={(e) => setForm({ ...form, voter_email: e.target.value })} placeholder="Email *" required className="rounded-xl border border-black/10 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                    <input data-testid="home-poll-phone" value={form.voter_phone} onChange={(e) => setForm({ ...form, voter_phone: e.target.value })} placeholder="Phone (optional)" className="rounded-xl border border-black/10 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                </div>
                                <button type="submit" disabled={submitting} data-testid="home-poll-submit" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-6 py-2.5 text-sm font-medium transition">
                                    <Vote className="w-4 h-4" strokeWidth={2} /> {submitting ? "Submitting…" : "Submit vote"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeaturedPoll;
